import { useEffect, useState } from 'react';
import { RefreshCw, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { listFlagged, removeQueuedCapture, retryQueuedCapture, type QueuedCapture } from '@/lib/offline';

/**
 * The flagged-capture list (BUILD_PLAN SC3 DELIVER): captures that
 * exhausted the offline queue's auto-retry budget (src/lib/offline/queue.ts,
 * MAX_AUTO_RETRY_ATTEMPTS) and need an operator decision, retry or
 * discard. Distinct from the quality gate, which never blocks; this list
 * is upload failure, not a quality opinion.
 */
export function FlaggedList() {
  const [items, setItems] = useState<QueuedCapture[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setItems(await listFlagged());
    setLoading(false);
  };

  useEffect(() => {
    void refresh();
  }, []);

  if (loading) {
    return <p className="text-sm text-muted">Loading flagged captures…</p>;
  }

  if (items.length === 0) {
    return <p className="text-sm text-muted">No flagged captures. Everything queued has uploaded or is pending.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((item) => (
        <Card key={item.localId}>
          <CardHeader className="flex-row items-center justify-between pb-2">
            <CardTitle>{item.mode}</CardTitle>
            <Badge tone="danger">
              {item.refusal ? 'Not sent' : `Failed after ${item.attempts} attempts`}
            </Badge>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-muted">{item.lastError ?? 'Upload failed for an unknown reason.'}</p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="min-h-11"
                onClick={() => {
                  void retryQueuedCapture(item.localId).then(refresh);
                }}
              >
                <RefreshCw />
                Retry
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="min-h-11"
                onClick={() => {
                  void removeQueuedCapture(item.localId).then(refresh);
                }}
              >
                <Trash2 />
                Discard
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
